import { useLandingDiscoveryFeatures } from 'app/hooks/use-landing-discovery-features';
import { useInsuredUsers } from 'app/components/insured-users';
import { useHideStepsToComplete } from 'app/components/steps-to-complete';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { useAllTags, type ClosestListingsByTag } from 'app/network/listing';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import { useNeighbourhoodStatus } from 'app/network/statistics';
import {
  useRecommendationRuntime,
  useUrgentNeedsRecommendationsWithRuntime,
  useRecentOffersRecommendationsWithRuntime,
  useRecentLoansRecommendationsWithRuntime,
  useMixedRecommendationsWithRuntime,
  useNearestSharingPlaceRecommendationsWithRuntime,
  useMatchingRequestsRecommendationsWithRuntime,
  useYourInterestRecommendationsWithRuntime,
  useMostPopularRecommendationsWithRuntime,
  useFavouritesRecommendationsWithRuntime,
} from 'app/screens/billboard/all-listings/components/recommended-listings';
import {
  Strategies,
  shouldShowRecommendationSection,
} from 'app/screens/billboard/all-listings/components/recommended-listings.utils';
import { ListingType } from 'app/types/listing';
import { QuickOfferBannerType } from 'app/types/preferences';
import { FeatureType } from 'app/types/recommender';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import React from 'react';
import { usePopularInterestsTags } from './components/popular-interests';
import {
  getOrderedGeneralInterestTagIds,
  getSeededUserInterestTagIds,
  getUserInterestTagIds,
} from './landing-interest-tags';
import { useLandingPageNavigationActions, useProfileCompletionSteps } from './shared';
import { useLandingPage } from './use-landing-page';

function useQuickOfferBanner(isLoggedIn: boolean, completeProfileSteps: ReturnType<typeof useProfileCompletionSteps>) {
  const addListingCompleted = completeProfileSteps.steps.find((step) => step.id === 'add-listing')?.completed ?? false;
  const { data: quickOfferPreferenceData, isPending: isQuickOfferPreferencePending } =
    useOnePreference('quickOfferBanner');
  const quickOfferPreference = quickOfferPreferenceData?.value ?? QuickOfferBannerType.nonHideable;
  const showTopMultipleOfferCard =
    isLoggedIn &&
    (!addListingCompleted || (!isQuickOfferPreferencePending && quickOfferPreference !== QuickOfferBannerType.hidden));
  const { mutate: setQuickOfferPreference } = useUpsertOnePreference('quickOfferBanner');
  const dismissTopMultipleOfferCard = () => {
    setQuickOfferPreference(QuickOfferBannerType.hidden);
  };
  return { showTopMultipleOfferCard, dismissTopMultipleOfferCard };
}

export function useDesktopLandingData() {
  const { showDiscoveryFeeds, showLandingCategories } = useLandingDiscoveryFeatures();
  const {
    profile,
    user,
    stats,
    userInterests,
    interestsAvailable,
    unreadData,
    isLoggedIn,
    freeSubAvailable,
    freeSubNumDaysLeft,
    hasCustomProfilePicture,
    showInviteOthersPrompt,
    unreadCount,
    activitySubtitle,
    greeting,
    addressLabel,
    completeProfilePromptCompleted,
    setCompleteProfilePromptCompleted,
    canPublish,
    canViewActivitySummary,
    lendings,
    borrowings,
    loansQuerySettled,
    visibleExpiredLoans,
    navigation,
    tRecommendations: t,
  } = useLandingPage();
  const actions = useLandingPageNavigationActions(navigation);
  const hideStepsToComplete = useHideStepsToComplete();
  const completeProfileSteps = useProfileCompletionSteps({
    actions,
    hasCustomProfilePicture,
    profile,
    setCompleteProfilePromptCompleted,
    user,
    userInterests,
  });
  const { showTopMultipleOfferCard, dismissTopMultipleOfferCard } = useQuickOfferBanner(
    isLoggedIn,
    completeProfileSteps,
  );
  const { data: tags, isPending: isTagsPending } = useAllTags(false, { enabled: interestsAvailable });
  useRegisterLoading(interestsAvailable && isTagsPending, 'LandingPageScreen-tags');
  const interestSelectionSeed = React.useId();
  const userInterestTagIds = getUserInterestTagIds(userInterests);
  const randomUserInterestTagIds = getSeededUserInterestTagIds(userInterestTagIds, 3, interestSelectionSeed);
  const visibleGeneralInterestTagIds = getOrderedGeneralInterestTagIds(userInterests, tags).slice(0, 3);
  const { canBecomeAmbassador } = useAmbassador();
  const recommendationRuntime = useRecommendationRuntime();
  const urgentNeedsRecommendations = useUrgentNeedsRecommendationsWithRuntime(
    { strategy: Strategies.UrgentNeeds },
    recommendationRuntime,
  );
  const urgentNeedsCount = urgentNeedsRecommendations.count;
  useRegisterLoading(urgentNeedsRecommendations.isPending, 'LandingPageScreen-urgent-needs');
  const recentOffersRecommendations = useRecentOffersRecommendationsWithRuntime(
    { strategy: Strategies.RecentOffers },
    recommendationRuntime,
  );
  useRegisterLoading(recentOffersRecommendations.isPending, 'LandingPageScreen-recent-offers');
  const recentLoansRecommendations = useRecentLoansRecommendationsWithRuntime(
    { strategy: Strategies.RecentLoans },
    recommendationRuntime,
  );
  const mixedRecommendations = useMixedRecommendationsWithRuntime(
    {
      strategy: Strategies.UseMixedRecommendations,
      recommendationType: ListingType.offer,
      similarityType: FeatureType.CONTENT,
    },
    recommendationRuntime,
  );
  const nearestSharingPlaceRecommendations = useNearestSharingPlaceRecommendationsWithRuntime(
    { strategy: Strategies.NearestSharingPlace },
    recommendationRuntime,
  );
  const matchingRequestsRecommendations = useMatchingRequestsRecommendationsWithRuntime(
    {
      strategy: Strategies.UseMatchNeedsWithOffers,
      recommendationType: ListingType.request,
      similarityType: FeatureType.CONTENT,
    },
    recommendationRuntime,
  );
  const randomInterestRecommendations = useYourInterestRecommendationsWithRuntime(
    {
      strategy: Strategies.YourInterest,
      tagIds: randomUserInterestTagIds,
      enabled: interestsAvailable && randomUserInterestTagIds.length > 0,
    },
    recommendationRuntime,
  );
  const mostPopularRecommendations = useMostPopularRecommendationsWithRuntime(
    {
      strategy: Strategies.MostPopular,
      recommendationType: ListingType.offer,
      similarityType: FeatureType.CONTENT,
    },
    recommendationRuntime,
  );
  const favouritesRecommendations = useFavouritesRecommendationsWithRuntime(
    { strategy: Strategies.YourFavourites },
    recommendationRuntime,
  );
  const generalInterestRecommendations = useYourInterestRecommendationsWithRuntime(
    {
      strategy: Strategies.YourInterest,
      tagIds: visibleGeneralInterestTagIds,
      enabled: interestsAvailable && visibleGeneralInterestTagIds.length > 0,
    },
    recommendationRuntime,
  );
  const popularInterestsQuery = usePopularInterestsTags({
    enabled: interestsAvailable && !showLandingCategories,
  });
  useRegisterLoading(
    randomInterestRecommendations.isPending || generalInterestRecommendations.isPending,
    'LandingPageScreen-your-interest',
  );
  const insuredUsers = useInsuredUsers(user, 'landing-page');
  const { data: neighbourhoodStatus, isPending: isNeighbourhoodStatusPending } = useNeighbourhoodStatus({
    enabled: isLoggedIn,
  });
  const isImmatureNeighbour = !isNeighbourhoodStatusPending && neighbourhoodStatus?.isMatureNeighbourhood === false;
  const visibleRandomInterestRecommendations = interestsAvailable
    ? (randomInterestRecommendations.recommendations as ClosestListingsByTag[] | undefined)?.slice(
        0,
        randomUserInterestTagIds.length,
      )
    : undefined;
  const visibleGeneralInterestRecommendations = interestsAvailable
    ? (generalInterestRecommendations.recommendations as ClosestListingsByTag[] | undefined)?.slice(
        0,
        visibleGeneralInterestTagIds.length,
      )
    : undefined;
  const showPopularInterests =
    interestsAvailable && (popularInterestsQuery.data === undefined || popularInterestsQuery.data.length > 0);

  return {
    showDiscoveryFeeds,
    showLandingCategories,
    profile,
    user,
    stats,
    unreadData,
    isLoggedIn,
    freeSubAvailable,
    freeSubNumDaysLeft,
    showInviteOthersPrompt,
    unreadCount,
    activitySubtitle,
    greeting,
    addressLabel,
    completeProfilePromptCompleted,
    canPublish,
    canViewActivitySummary,
    lendings,
    borrowings,
    loansQuerySettled,
    visibleExpiredLoans,
    navigation,
    t,
    actions,
    hideStepsToComplete,
    completeProfileSteps,
    showTopMultipleOfferCard,
    dismissTopMultipleOfferCard,
    tags,
    canBecomeAmbassador,
    urgentNeedsRecommendations,
    urgentNeedsCount,
    recentOffersRecommendations,
    recentLoansRecommendations,
    mixedRecommendations,
    nearestSharingPlaceRecommendations,
    matchingRequestsRecommendations,
    mostPopularRecommendations,
    favouritesRecommendations,
    popularInterestsQuery,
    insuredUsers,
    neighbourhoodStatus,
    isNeighbourhoodStatusPending,
    isImmatureNeighbour,
    shouldShowRecommendationSection,
    visibleRandomInterestRecommendations,
    visibleGeneralInterestRecommendations,
    showPopularInterests,
  };
}
